import SectionHead from "./SectionHead";
import { shelfSections } from "@/mocks/homeContent";

function FeatureCard({
  code,
  name,
  desc,
  tags,
  count,
  wide,
}: {
  code: string;
  name: string;
  desc: string;
  tags: string[];
  count: string;
  wide: boolean;
}) {
  return (
    <a
      href="#collections"
      className={`group relative flex flex-col overflow-hidden rounded-xl border border-[#e0d4be] bg-white p-6 transition-all duration-300 hover:-translate-y-1 md:p-7 ${
        wide ? 'md:col-span-7' : 'md:col-span-5'
      }`}
    >
      <span className="absolute right-0 top-0 h-20 w-20 translate-x-6 -translate-y-6 rounded-full bg-[#f0e4c8]" aria-hidden="true" />
      <div className="relative flex items-center justify-between">
        <span className="font-editorial text-2xl italic text-[#c8a45f]">{code}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#f3e9d2] px-3 py-1 text-[11px] font-semibold text-[#a1762f]">
          <i className="ri-star-fill text-[11px]" aria-hidden="true" />
          重点分区
        </span>
      </div>
      <h3 className="relative mt-4 text-xl font-bold text-[#241f18] md:text-2xl">
        {name}
      </h3>
      <p className="relative mt-2 max-w-md text-[14px] leading-relaxed text-[#6d6149]">
        {desc}
      </p>
      <div className="relative mt-4 flex flex-wrap items-center gap-2">
        {tags.map((t) => (
          <span
            key={t}
            className="whitespace-nowrap rounded-md bg-[#f0eadb] px-2.5 py-1 text-[12px] text-[#6d6149]"
          >
            {t}
          </span>
        ))}
      </div>
      <div className="relative mt-6 flex items-center justify-between border-t border-[#ece2cd] pt-4">
        <span className="text-[13px] font-semibold text-[#a1762f]">{count} · 持续补录</span>
        <span className="flex items-center gap-1 text-sm font-semibold text-[#201b15] transition-colors group-hover:text-[#b3873a]">
          去翻
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </span>
      </div>
    </a>
  );
}

function NormalCard({
  code,
  name,
  desc,
  tags,
  count,
}: {
  code: string;
  name: string;
  desc: string;
  tags: string[];
  count: string;
}) {
  return (
    <a
      href="#collections"
      className="group flex flex-col rounded-xl border border-[#e0d4be] bg-[#f7f1e3] p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-white md:col-span-4"
    >
      <div className="flex items-center justify-between">
        <span className="font-editorial text-lg italic text-[#b3873a]">{code}</span>
        <i className="ri-arrow-right-up-line text-[#b3873a] opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
      </div>
      <h3 className="mt-3 text-[17px] font-bold text-[#241f18]">{name}</h3>
      <p className="mt-2 flex-1 text-[13px] leading-relaxed text-[#6d6149]">{desc}</p>
      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        {tags.map((t) => (
          <span
            key={t}
            className="whitespace-nowrap rounded-md bg-[#ece2cd] px-2 py-0.5 text-[11px] text-[#6d6149]"
          >
            {t}
          </span>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-[#e4d9c4] pt-3">
        <span className="text-[12px] font-semibold text-[#a1762f]">{count}</span>
        <span className="text-[12px] font-semibold text-[#201b15]">翻这一区</span>
      </div>
    </a>
  );
}

export default function ShelfIndex() {
  const features = shelfSections.filter((s) => s.featured);
  const normals = shelfSections.filter((s) => !s.featured);

  return (
    <section id="shelf" className="w-full scroll-mt-24">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-16 md:px-6 md:py-24">
        <div className="flex w-full flex-col items-start justify-between gap-5 md:flex-row md:items-end">
          <SectionHead
            eyebrow="SHELF INDEX · 宝库分区"
            title="分区索引，先把目录摊开"
            desc="黑料舍的存货按主题收进不同分区，从首页到旧档都码得整整齐齐。先挑一个分区，再顺着标签一步步收到你想要的档目。"
          />
          <a
            href="#guide"
            className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-[#d8cbb1] px-4 py-2 text-sm font-semibold text-[#201b15] transition-colors hover:border-[#b3873a] hover:text-[#b3873a] md:inline-flex"
          >
            <i className="ri-question-line" aria-hidden="true" />
            分区怎么用
          </a>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-12">
          {features.map((s, i) => (
            <FeatureCard key={s.code} {...s} wide={i === 0} />
          ))}
          {normals.map((s) => (
            <NormalCard key={s.code} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}